import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, 
  Package, 
  Truck, 
  CheckCircle, 
  Clock, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  Check, 
  X, 
  X as CloseIcon,
  Building2,
  User,
  Users,
  Calendar,
  DollarSign,
  FileText,
  Home,
  Phone,
  Mail,
  Tag,
  BarChart3,
  Receipt,
  Layers,
  ShoppingBag,
  ArrowLeft,
  Printer,
  ArrowRight,
  Loader2,
  CreditCard,
  Save,
  Calculator
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import { toast } from 'react-hot-toast';

const PurchaseManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency, currencySymbol } = useSettings();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseStats, setPurchaseStats] = useState({
    total_purchases: 0,
    pending_purchases: 0,
    approved_purchases: 0,
    received_purchases: 0,
    total_value: 0,
    pending_value: 0,
    approved_value: 0,
    received_value: 0
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);

  // Approve modal state
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveSuccess, setApproveSuccess] = useState(false);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [selectedAction, setSelectedAction] = useState('');
  
  // Receipt modal state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  
  // View modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  
  // Header approve modal state
  const [showHeaderApproveModal, setShowHeaderApproveModal] = useState(false);
  const [headerApproveLoading, setHeaderApproveLoading] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [selectedOrdersForApproval, setSelectedOrdersForApproval] = useState([]);
  
  // User menu state

  // Company settings state
  const [companySettings, setCompanySettings] = useState({
    company_name: 'NEXAPOS',
    company_address: '123 Business Street, Accra',
    company_phone: '+233 24 123 4567',
    tax_number: ''
  });


  // Form state for create modal
  const [formData, setFormData] = useState({
    supplier_id: '',
    expected_delivery_date: '',
    mode_of_purchase: '',
    remark: '',
    items: []
  });
  const [products, setProducts] = useState([]);
  const [poNumber, setPoNumber] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [outstandingBalance, setOutstandingBalance] = useState(0);
  
  // Product search states
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searching, setSearching] = useState(false);
  const [currentQuantity, setCurrentQuantity] = useState(0);

  // Supplier search states
  const [supplierSearchQuery, setSupplierSearchQuery] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [supplierSelectedIndex, setSupplierSelectedIndex] = useState(-1);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);



  // Fetch purchases
  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(supplierFilter && { supplier_id: supplierFilter })
      });

      const response = await apiService.request(`/purchases?${params}`);
      setPurchases(response.data.data);
      setTotalPages(response.data.last_page);
      setTotalItems(response.data.total);
      setError(null);
    } catch (err) {
      setError('Failed to fetch purchases');
      console.error('Error fetching purchases:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      const response = await apiService.getAllSuppliers();
      setSuppliers(response.data);
      console.log('Suppliers set:', response.data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  // Fetch company settings
  const fetchCompanySettings = async () => {
    try {
      const response = await apiService.getSettings();
      setCompanySettings({
        company_name: response.data.company_name || 'NEXAPOS',
        company_address: response.data.company_address || '123 Business Street, Accra',
        company_phone: response.data.company_phone || '+233 24 123 4567',
        tax_number: response.data.tax_number || ''
      });
    } catch (err) {
      console.error('Error fetching company settings:', err);
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    try {
      const response = await apiService.request('/products');
      setProducts(response.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  // Fetch purchase statistics
  const fetchPurchaseStats = async () => {
    try {
      const response = await apiService.request('/purchases-stats');
      setPurchaseStats(response.data);
    } catch (err) {
      console.error('Error fetching purchase stats:', err);
    }
  };

  // Effects
  useEffect(() => {
    fetchPurchases();
  }, [currentPage, perPage, searchTerm, statusFilter, supplierFilter]);

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
    fetchPurchaseStats();
    fetchCompanySettings();
  }, []);

  // Initialize filtered suppliers when suppliers are loaded
  useEffect(() => {
    if (suppliers.length > 0) {
      setFilteredSuppliers(suppliers);
    }
  }, [suppliers]);

  // Handle clicking outside supplier dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSupplierDropdown && !event.target.closest('.supplier-dropdown-container')) {
        setShowSupplierDropdown(false);
        setSupplierSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSupplierDropdown]);


  // Handlers
  const handleCardClick = (href) => {
    navigate(href);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleSupplierFilter = (supplierId) => {
    setSupplierFilter(supplierId);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  // PO Number generation
  const generatePONumber = async () => {
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    setPoNumber('Generating...');
    
    // Try to get the proper sequence number from API
    try {
      const response = await apiService.request(`/purchases/next-po-id?date=${today}`);
      
      // Handle different response formats
      let poNumber = null;
      
      if (response && response.po_number) {
        poNumber = response.po_number;
      } else if (response && response.data && response.data.po_number) {
        poNumber = response.data.po_number;
      }
      
      if (poNumber) {
        setPoNumber(poNumber);
      } else {
        // Fallback: calculate next sequence from existing purchases
        await generatePONumberFromExisting();
      }
    } catch (error) {
      // Fallback to calculating from existing purchases if API fails
      console.error('Failed to generate PO number:', error);
      await generatePONumberFromExisting();
    }
  };

  // Fallback PO number generation from existing purchases
  const generatePONumberFromExisting = async () => {
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const todayWithDash = new Date().toISOString().slice(0, 10); // Keep original format for dash version
    const todayPrefixNoDash = `PO-${today}`;
    const todayPrefixWithDash = `PO-${todayWithDash}-`;
    
    try {
      // Get all purchases to find the highest sequence number for today
      const response = await apiService.request('/purchases?per_page=1000'); // Get more records to ensure we find all today's orders
      const allPurchases = response.data?.data || [];
      
      console.log('All purchases:', allPurchases);
      
      // Filter purchases for today (both formats)
      const todayPurchases = allPurchases.filter(purchase => {
        const poNumber = purchase.purchase_order_number;
        return poNumber && (
          poNumber.startsWith(todayPrefixNoDash) || 
          poNumber.startsWith(todayPrefixWithDash)
        );
      });
      
      console.log('Today\'s purchases:', todayPurchases);
      
      // Extract sequence numbers from both formats
      const sequenceNumbers = todayPurchases.map(purchase => {
        const poNumber = purchase.purchase_order_number;
        let sequenceStr = '';
        
        if (poNumber.startsWith(todayPrefixWithDash)) {
          // Format: PO-2025-09-25-001
          sequenceStr = poNumber.replace(todayPrefixWithDash, '');
        } else if (poNumber.startsWith(todayPrefixNoDash)) {
          // Format: PO-20250925001
          sequenceStr = poNumber.replace(todayPrefixNoDash, '');
        }
        
        return parseInt(sequenceStr) || 0;
      });
      
      console.log('Sequence numbers:', sequenceNumbers);
      
      // Find the highest sequence number
      const maxSequence = sequenceNumbers.length > 0 ? Math.max(...sequenceNumbers) : 0;
      const nextSequence = maxSequence + 1;
      
      // Use the format without dash (consistent with recent entries)
      const fallbackPO = `${todayPrefixNoDash}${nextSequence.toString().padStart(3, '0')}`;
      
      console.log('Generated fallback PO number:', fallbackPO);
      setPoNumber(fallbackPO);
      
    } catch (error) {
      console.log('Failed to get existing purchases, using default:', error);
      // Ultimate fallback
      const fallbackPO = `${todayPrefixNoDash}001`;
      console.log('Using ultimate fallback PO number:', fallbackPO);
      setPoNumber(fallbackPO);
    }
  };

  // Create modal handlers
  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    setCreateSuccess(false);
    setPoNumber(''); // Clear previous PO number
    generatePONumber(); // Generate new PO number
    setFormData({
      supplier_id: '',
      expected_delivery_date: '',
      mode_of_purchase: '',
      remark: '',
      items: []
    });
    // Reset supplier search state
    setSupplierSearchQuery('');
    setShowSupplierDropdown(false);
    setSupplierSelectedIndex(-1);
    setFilteredSuppliers([]);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateSuccess(false);
    setPoNumber(''); // Clear PO number when closing modal
    setSelectedSupplier(null);
    setOutstandingBalance(0);
    setSearchQuery('');
    setShowProductDropdown(false);
    setSelectedIndex(-1);
    setSelectedProduct(null);
    setCurrentQuantity(0);
    // Reset supplier search state
    setSupplierSearchQuery('');
    setShowSupplierDropdown(false);
    setSupplierSelectedIndex(-1);
    setFilteredSuppliers([]);
    setFormData({
      supplier_id: '',
      expected_delivery_date: '',
      mode_of_purchase: '',
      remark: '',
      items: []
    });
  };

  // Approve modal handlers
  const handleOpenApproveModal = (purchaseOrder) => {
    setSelectedPurchaseOrder(purchaseOrder);
    setShowApproveModal(true);
    setApproveSuccess(false);
    setSelectedAction('');
  };

  const handleCloseApproveModal = () => {
    setShowApproveModal(false);
    setApproveSuccess(false);
    setSelectedPurchaseOrder(null);
    setSelectedAction('');
  };

  // Receipt modal handlers
  const handleOpenReceiptModal = (orderData) => {
    setReceiptData(orderData);
    setShowReceiptModal(true);
  };

  const handleCloseReceiptModal = () => {
    setShowReceiptModal(false);
    setReceiptData(null);
  };

  const handlePrintReceipt = () => {
    // Create a temporary div with the receipt content
    const printContent = document.createElement('div');
    printContent.innerHTML = `
      <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white;">
        <!-- Header Section -->
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2563eb; padding-bottom: 20px;">
          <h1 style="font-size: 32px; font-weight: bold; color: #1f2937; margin: 0 0 8px 0; letter-spacing: 2px;">${companySettings.company_name}</h1>
          <p style="font-size: 16px; color: #6b7280; margin: 0 0 15px 0; font-weight: 500;">Purchase Order ${receiptData.status === 'approved' ? 'Approved' : receiptData.status === 'received' ? 'Received' : 'Pending'} Receipt</p>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: #6b7280;">
            <span><strong>PO Number:</strong> ${receiptData.purchase_order_number}</span>
            <span><strong>Date:</strong> ${new Date(receiptData.created_at).toLocaleDateString('en-US')}</span>
            <span><strong>Time:</strong> ${new Date(receiptData.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
            <span><strong>Processed by:</strong> ${user?.name || 'Agent'}</span>
          </div>
        </div>

        <!-- Supplier and Company Section -->
        <div style="margin-bottom: 30px; display: flex; gap: 30px;">
          <!-- Supplier Section (Left) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Supplier Details</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${receiptData.supplier?.name || 'Unknown Supplier'}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📞 ${receiptData.supplier?.phone || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">✉️ ${receiptData.supplier?.email || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280;">📍 ${receiptData.supplier?.address || 'N/A'}</div>
            </div>
          </div>
          
          <!-- Company Section (Right) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Company Details</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${companySettings.company_name}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📍 ${companySettings.company_address}</div>
              <div style="font-size: 14px; color: #6b7280;">📞 ${companySettings.company_phone}</div>
              ${companySettings.tax_number ? `<div style="font-size: 14px; color: #6b7280;">Tax ID: ${companySettings.tax_number}</div>` : ''}
            </div>
          </div>
        </div>

        <!-- Order Information Table -->
        <div style="margin-bottom: 30px;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Order Information</h3>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
            <thead>
              <tr style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);">
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-weight: bold; color: #374151; font-size: 14px;">${receiptData.status === 'received' ? 'Received Date' : 'Expected Delivery'}</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Mode of Purchase</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Status</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Agent</th>
              </tr>
            </thead>
            <tbody>
              <tr style="background: #f8fafc;">
                <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: 500; color: #1f2937; font-size: 14px;">${receiptData.status === 'received' ? (receiptData.received_date ? new Date(receiptData.received_date).toLocaleDateString('en-US') : new Date().toLocaleDateString('en-US')) : (receiptData.expected_delivery_date ? new Date(receiptData.expected_delivery_date).toLocaleDateString('en-US') : 'N/A')}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: 500; color: #1f2937; font-size: 14px; text-transform: capitalize;">${receiptData.mode_of_purchase?.replace('_', ' ') || 'N/A'}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #059669; font-size: 14px; font-weight: bold;">${receiptData.status === 'approved' ? 'Approved' : receiptData.status === 'received' ? 'Received' : 'Pending'}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; color: #374151; font-size: 14px;">${receiptData.agent || user?.name || 'System User'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Order Items Table -->
        <div style="margin-bottom: 30px;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Order Items</h3>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
            <thead>
              <tr style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);">
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-weight: bold; color: #374151; font-size: 14px;">Product</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Quantity</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">UOM</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Price</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Amount</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${receiptData.items?.map(item => `
                <tr style="background: #f8fafc;">
                  <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: 500; color: #1f2937; font-size: 14px;">${item.product?.name || 'Unknown Product'}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #1f2937; font-size: 14px;">${item.quantity}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #374151; font-size: 14px;">${item.uom || 'pcs'}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #1f2937; font-size: 14px;">${formatCurrency(item.cost_price)}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #1f2937; font-size: 14px;">${formatCurrency(item.subtotal)}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #059669; font-size: 14px; font-weight: bold;">${receiptData.status === 'approved' ? 'Approved' : receiptData.status === 'received' ? 'Received' : 'Pending'}</td>
                </tr>
              `).join('') || '<tr><td colspan="6" style="text-align: center; padding: 20px; color: #6b7280;">No items found</td></tr>'}
            </tbody>
          </table>
        </div>

        <!-- Order Summary -->
        <div style="background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); padding: 25px; border-radius: 12px; border: 1px solid #e2e8f0;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 20px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Order Summary</h3>
          <div style="space-y: 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 15px 0; border-top: 2px solid #2563eb; margin-top: 15px;">
              <span style="font-size: 20px; font-weight: bold; color: #1f2937;">Total Amount:</span>
              <span style="font-size: 24px; font-weight: bold; color: #2563eb;">${formatCurrency(receiptData.total_cost)}</span>
            </div>
          </div>
        </div>

        ${receiptData.remark ? `
        <!-- Notes -->
        <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0; margin: 20px 0;">
          <h3 style="font-size: 16px; font-weight: bold; color: #1f2937; margin: 0 0 10px 0;">Notes</h3>
          <p style="font-size: 14px; color: #6b7280; margin: 0;">${receiptData.remark}</p>
        </div>
        ` : ''}

        <!-- Footer -->
        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 2px solid #e2e8f0; color: #6b7280; font-size: 12px;">
          <p style="margin: 0 0 8px 0;">Thank you for your business!</p>
          <p style="margin: 0 0 8px 0;">Keep this receipt for your records</p>
          <p style="margin: 0; font-weight: bold;">Powered By SherSoft Technologies - 0548104944</p>
        </div>
      </div>
    `;
    
    // Create hidden iframe for printing without opening new tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
        <html>
        <head>
          <title>Purchase Order Receipt - ${receiptData.purchase_order_number}</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 0;
              background: white;
              color: #333;
            }
            .receipt { 
              max-width: 800px; 
              margin: 0 auto; 
              padding: 20px;
              background: white;
            }
            @media print {
              body { margin: 0; padding: 0; }
              .receipt { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">${printContent.innerHTML}</div>
        </body>
        </html>
      `);
    iframeDoc.close();
    
    // Wait for content to load, then print
    iframe.onload = () => {
      iframe.contentWindow.print();
      // Clean up iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
    
    // Show print notification
    toast.success('Purchase order receipt sent to printer!', {
      duration: 2000,
      icon: '🖨️',
      style: {
        background: '#10B981',
        color: '#fff',
        fontSize: '14px',
        padding: '12px',
        borderRadius: '8px',
      },
    });
  };

  const generatePDF = () => {
    // Create a temporary div with the receipt content
    const printContent = document.createElement('div');
    printContent.innerHTML = `
      <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white;">
        <!-- Header Section -->
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2563eb; padding-bottom: 20px;">
          <h1 style="font-size: 32px; font-weight: bold; color: #1f2937; margin: 0 0 8px 0; letter-spacing: 2px;">${companySettings.company_name}</h1>
          <p style="font-size: 16px; color: #6b7280; margin: 0 0 15px 0; font-weight: 500;">Purchase Order ${receiptData.status === 'approved' ? 'Approved' : receiptData.status === 'received' ? 'Received' : 'Pending'} Receipt</p>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: #6b7280;">
            <span><strong>PO Number:</strong> ${receiptData.purchase_order_number}</span>
            <span><strong>Date:</strong> ${new Date(receiptData.created_at).toLocaleDateString('en-US')}</span>
            <span><strong>Time:</strong> ${new Date(receiptData.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
            <span><strong>Processed by:</strong> ${user?.name || 'Agent'}</span>
          </div>
        </div>

        <!-- Supplier and Company Section -->
        <div style="margin-bottom: 30px; display: flex; gap: 30px;">
          <!-- Supplier Section (Left) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Supplier Details</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${receiptData.supplier?.name || 'Unknown Supplier'}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📞 ${receiptData.supplier?.phone || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">✉️ ${receiptData.supplier?.email || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280;">📍 ${receiptData.supplier?.address || 'N/A'}</div>
            </div>
          </div>
          
          <!-- Company Section (Right) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Company Details</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${companySettings.company_name}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📍 ${companySettings.company_address}</div>
              <div style="font-size: 14px; color: #6b7280;">📞 ${companySettings.company_phone}</div>
              ${companySettings.tax_number ? `<div style="font-size: 14px; color: #6b7280;">Tax ID: ${companySettings.tax_number}</div>` : ''}
            </div>
          </div>
        </div>

        <!-- Order Information Table -->
        <div style="margin-bottom: 30px;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Order Information</h3>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
            <thead>
              <tr style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);">
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-weight: bold; color: #374151; font-size: 14px;">${receiptData.status === 'received' ? 'Received Date' : 'Expected Delivery'}</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Mode of Purchase</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Status</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Agent</th>
              </tr>
            </thead>
            <tbody>
              <tr style="background: #f8fafc;">
                <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: 500; color: #1f2937; font-size: 14px;">${receiptData.status === 'received' ? (receiptData.received_date ? new Date(receiptData.received_date).toLocaleDateString('en-US') : new Date().toLocaleDateString('en-US')) : (receiptData.expected_delivery_date ? new Date(receiptData.expected_delivery_date).toLocaleDateString('en-US') : 'N/A')}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: 500; color: #1f2937; font-size: 14px; text-transform: capitalize;">${receiptData.mode_of_purchase?.replace('_', ' ') || 'N/A'}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #059669; font-size: 14px; font-weight: bold;">${receiptData.status === 'approved' ? 'Approved' : receiptData.status === 'received' ? 'Received' : 'Pending'}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; color: #374151; font-size: 14px;">${receiptData.agent || user?.name || 'System User'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Order Items Table -->
        <div style="margin-bottom: 30px;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Order Items</h3>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
            <thead>
              <tr style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);">
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-weight: bold; color: #374151; font-size: 14px;">Product</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Quantity</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">UOM</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Price</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Amount</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${receiptData.items?.map(item => `
                <tr style="background: #f8fafc;">
                  <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: 500; color: #1f2937; font-size: 14px;">${item.product?.name || 'Unknown Product'}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #1f2937; font-size: 14px;">${item.quantity}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #374151; font-size: 14px;">${item.uom || 'pcs'}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #1f2937; font-size: 14px;">${formatCurrency(item.cost_price)}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #1f2937; font-size: 14px;">${formatCurrency(item.subtotal)}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #059669; font-size: 14px; font-weight: bold;">${receiptData.status === 'approved' ? 'Approved' : receiptData.status === 'received' ? 'Received' : 'Pending'}</td>
                </tr>
              `).join('') || '<tr><td colspan="6" style="text-align: center; padding: 20px; color: #6b7280;">No items found</td></tr>'}
            </tbody>
          </table>
        </div>

        <!-- Order Summary -->
        <div style="background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); padding: 25px; border-radius: 12px; border: 1px solid #e2e8f0;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 20px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Order Summary</h3>
          <div style="space-y: 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 15px 0; border-top: 2px solid #2563eb; margin-top: 15px;">
              <span style="font-size: 20px; font-weight: bold; color: #1f2937;">Total Amount:</span>
              <span style="font-size: 24px; font-weight: bold; color: #2563eb;">${formatCurrency(receiptData.total_cost)}</span>
            </div>
          </div>
        </div>

        ${receiptData.remark ? `
        <!-- Notes -->
        <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0; margin: 20px 0;">
          <h3 style="font-size: 16px; font-weight: bold; color: #1f2937; margin: 0 0 10px 0;">Notes</h3>
          <p style="font-size: 14px; color: #6b7280; margin: 0;">${receiptData.remark}</p>
        </div>
        ` : ''}

        <!-- Footer -->
        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 2px solid #e2e8f0; color: #6b7280; font-size: 12px;">
          <p style="margin: 0 0 8px 0;">Thank you for your business!</p>
          <p style="margin: 0 0 8px 0;">Keep this receipt for your records</p>
          <p style="margin: 0; font-weight: bold;">Powered By SherSoft Technologies - 0548104944</p>
        </div>
      </div>
    `;
    
    // Create hidden iframe for PDF generation without opening new tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>Purchase Order Receipt - ${receiptData.purchase_order_number}</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 0;
              background: white;
              color: #333;
            }
            .receipt { 
              max-width: 800px; 
              margin: 0 auto; 
              padding: 20px;
              background: white;
            }
            @media print {
              body { margin: 0; padding: 0; }
              .receipt { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">${printContent.innerHTML}</div>
        </body>
      </html>
    `);
    iframeDoc.close();
    
    // Wait for content to load, then trigger print dialog for PDF generation
    iframe.onload = () => {
      iframe.contentWindow.print();
      // Clean up iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
    
    // Show PDF generation notification
    toast.success('PDF ready for download. Use "Save as PDF" in print dialog.', {
      duration: 5000,
      icon: '📄',
      style: {
        background: '#3B82F6',
        color: '#fff',
        fontSize: '14px',
        padding: '12px',
        borderRadius: '8px',
      },
    });
  };

  const handleSendWhatsApp = () => {
    if (!receiptData?.supplier?.phone) {
      toast.error('Supplier phone number not available', {
        duration: 3000,
      });
      return;
    }

    // Format phone number (remove any non-digit characters and add country code if needed)
    let phoneNumber = receiptData.supplier.phone.replace(/\D/g, '');
    if (phoneNumber.startsWith('0')) {
      phoneNumber = '233' + phoneNumber.substring(1); // Ghana country code
    } else if (!phoneNumber.startsWith('233')) {
      phoneNumber = '233' + phoneNumber;
    }

    const message = `Hello ${receiptData.supplier.name},\n\nWe are pleased to inform you that your purchase order ${receiptData.purchase_order_number} has been approved by our management.\n\nPlease find the official purchase order receipt attached in PDF format. Kindly review the order details and prepare the items for delivery as specified.\n\nExpected delivery date: ${receiptData.expected_delivery_date ? new Date(receiptData.expected_delivery_date).toLocaleDateString('en-GB') : 'To be confirmed'}\n\nIf you have any questions regarding this order, please do not hesitate to contact us.\n\nWe look forward to receiving the goods.\n\nBest regards,\nNEXAPOS Procurement Team`;
    
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    toast.success('WhatsApp opened with supplier contact. Generate PDF first to attach.', {
      duration: 4000,
    });
  };

  // View modal handlers
  const handleOpenViewModal = (purchaseData) => {
    setViewData(purchaseData);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewData(null);
  };

  // Header approve modal handlers
  const handleOpenHeaderApproveModal = () => {
    // Filter pending orders
    const pending = purchases.filter(purchase => purchase.status === 'pending');
    setPendingOrders(pending);
    setSelectedOrdersForApproval([]);
    setShowHeaderApproveModal(true);
  };

  const handleCloseHeaderApproveModal = () => {
    setShowHeaderApproveModal(false);
    setPendingOrders([]);
    setSelectedOrdersForApproval([]);
  };

  const handleSelectOrderForApproval = (orderId) => {
    setSelectedOrdersForApproval(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSelectAllOrders = () => {
    if (selectedOrdersForApproval.length === pendingOrders.length) {
      setSelectedOrdersForApproval([]);
    } else {
      setSelectedOrdersForApproval(pendingOrders.map(order => order.id));
    }
  };

  // Reset form for next order
  const resetFormForNextOrder = () => {
    // Reset all form states
    setSelectedSupplier(null);
    setOutstandingBalance(0);
    setSearchQuery('');
    setShowProductDropdown(false);
    setSelectedIndex(-1);
    setSelectedProduct(null);
    setCurrentQuantity(0);
    setFormData({
      supplier_id: '',
      expected_delivery_date: '',
      mode_of_purchase: '',
      remark: '',
      items: []
    });
    
    // Reset form inputs
    const quantityInput = document.getElementById('purchase-quantity-input');
    const costPriceInput = document.getElementById('purchase-cost-price-input');
    if (quantityInput) quantityInput.value = '1';
    if (costPriceInput) costPriceInput.value = '0';
    
    // Generate next PO number immediately
      console.log('Generating next PO number after order creation...');
      generatePONumber();
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Supplier search functionality
  const searchSuppliers = useCallback((query) => {
    if (!query.trim()) {
      setFilteredSuppliers(suppliers);
      return;
    }

    const filtered = suppliers.filter(supplier =>
      supplier.name.toLowerCase().includes(query.toLowerCase()) ||
      supplier.supplierID?.toLowerCase().includes(query.toLowerCase()) ||
      supplier.phone?.includes(query)
    );
    
    setFilteredSuppliers(filtered);
  }, [suppliers]);

  // Handle supplier search input change
  const handleSupplierSearchChange = useCallback((e) => {
    const query = e.target.value;
    setSupplierSearchQuery(query);
    setSupplierSelectedIndex(-1);
    searchSuppliers(query);
  }, [searchSuppliers]);

  // Handle supplier selection
  const handleSupplierSelect = useCallback((supplier) => {
    setSupplierSearchQuery(supplier.name);
    setSelectedSupplier(supplier);
    setFilteredSuppliers([]);
    setShowSupplierDropdown(false);
    setSupplierSelectedIndex(-1);
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      supplier_id: supplier.id
    }));

    // Fetch outstanding balance
    fetchSupplierBalance(supplier.id);
  }, []);

  // Fetch supplier balance
  const fetchSupplierBalance = async (supplierId) => {
    try {
      const balanceResponse = await apiService.getSupplierOutstandingBalance(supplierId);
      if (balanceResponse && balanceResponse.data && balanceResponse.data.outstanding_balance !== undefined) {
        const balance = parseFloat(balanceResponse.data.outstanding_balance) || 0;
        setOutstandingBalance(balance);
      } else {
        const supplier = suppliers.find(s => s.id == supplierId);
        const balance = parseFloat(supplier?.balance) || 0;
        setOutstandingBalance(balance);
      }
    } catch (balanceError) {
      console.error('Error fetching outstanding balance:', balanceError);
      const supplier = suppliers.find(s => s.id == supplierId);
      const balance = parseFloat(supplier?.balance) || 0;
      setOutstandingBalance(balance);
    }
  };

  // Handle supplier key navigation
  const handleSupplierKeyDown = useCallback((e) => {
    if (!showSupplierDropdown || filteredSuppliers.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = supplierSelectedIndex < filteredSuppliers.length - 1 ? supplierSelectedIndex + 1 : 0;
        setSupplierSelectedIndex(nextIndex);
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = supplierSelectedIndex > 0 ? supplierSelectedIndex - 1 : filteredSuppliers.length - 1;
        setSupplierSelectedIndex(prevIndex);
        break;
      case 'Enter':
        e.preventDefault();
        if (supplierSelectedIndex >= 0 && supplierSelectedIndex < filteredSuppliers.length) {
          handleSupplierSelect(filteredSuppliers[supplierSelectedIndex]);
        }
        break;
      case 'Escape':
        setShowSupplierDropdown(false);
        setSupplierSelectedIndex(-1);
        break;
    }
  }, [showSupplierDropdown, filteredSuppliers, supplierSelectedIndex, handleSupplierSelect]);

  // Handle supplier search focus
  const handleSupplierSearchFocus = useCallback(() => {
    if (supplierSearchQuery.trim().length >= 1) {
      setShowSupplierDropdown(true);
    }
  }, [supplierSearchQuery]);

  // Handle supplier selection and auto-populate supplier information
  const handleSupplierChange = async (supplierId) => {
    console.log('Supplier ID selected:', supplierId);
    console.log('Available suppliers:', suppliers);
    
    setFormData(prev => ({
      ...prev,
      supplier_id: supplierId
    }));

    if (supplierId) {
      try {
        // Find the selected supplier from the suppliers list
        const supplier = suppliers.find(s => s.id == supplierId); // Use == for type coercion
        console.log('Found supplier:', supplier);
        
        if (supplier) {
          setSelectedSupplier(supplier);
          console.log('Selected supplier set:', supplier);
          
          // Fetch outstanding balance
          try {
            const balanceResponse = await apiService.getSupplierOutstandingBalance(supplierId);
            if (balanceResponse && balanceResponse.data && balanceResponse.data.outstanding_balance !== undefined) {
              const balance = parseFloat(balanceResponse.data.outstanding_balance) || 0;
              setOutstandingBalance(balance);
              console.log('Outstanding balance:', balance);
            } else {
              // Fallback to supplier's balance field
              const balance = parseFloat(supplier.balance) || 0;
              setOutstandingBalance(balance);
              console.log('Using supplier balance:', balance);
            }
          } catch (balanceError) {
            console.error('Error fetching outstanding balance:', balanceError);
            // Fallback to supplier's balance field
            const balance = parseFloat(supplier.balance) || 0;
            setOutstandingBalance(balance);
          }
        } else {
          console.log('No supplier found with ID:', supplierId);
        }
      } catch (error) {
        console.error('Error fetching supplier details:', error);
      }
    } else {
      setSelectedSupplier(null);
      setOutstandingBalance(0);
    }
  };

  // Product search functionality
  const searchProducts = useCallback(async (query) => {
    if (!query.trim()) {
      setProducts([]);
      setShowProductDropdown(false);
      return;
    }

    setSearching(true);
    try {
      const response = await apiService.searchProducts(query);
      const data = response.data || response;
      setProducts(data.products || []);
      setShowProductDropdown(true);
    } catch (error) {
      console.error('Error searching products:', error);
      setProducts([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSelectedIndex(-1);
    
    if (query.trim().length >= 2) {
      searchProducts(query);
    } else {
      setProducts([]);
      setShowProductDropdown(false);
    }
  }, [searchProducts]);

  // Handle product selection
  const handleProductSelect = useCallback((product) => {
    setSearchQuery(product.name);
    setSelectedProduct(product);
    setProducts([]);
    setShowProductDropdown(false);
    setSelectedIndex(-1);
    
    // Set current quantity state
    const qty = product.quantity || 0;
    setCurrentQuantity(qty);
    
    // Set cost price in the cost price field
    const costPriceInput = document.getElementById('purchase-cost-price-input');
    if (costPriceInput) {
      const price = parseFloat(product.cost_price || product.selling_price || product.price || 0);
      costPriceInput.value = price;
    }
    
    // Focus on quantity field and select the default value
    setTimeout(() => {
      const quantityInput = document.getElementById('purchase-quantity-input');
      if (quantityInput) {
        quantityInput.focus();
        quantityInput.select();
      }
    }, 100);
  }, []);

  // Handle key navigation in search
  const handleKeyDown = useCallback((e) => {
    if (!showProductDropdown || products.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = selectedIndex < products.length - 1 ? selectedIndex + 1 : 0;
        setSelectedIndex(nextIndex);
        // Scroll selected item into view
        setTimeout(() => {
          const selectedElement = document.querySelector(`[data-index="${nextIndex}"]`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ 
              block: 'nearest', 
              behavior: 'smooth' 
            });
          }
        }, 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = selectedIndex > 0 ? selectedIndex - 1 : products.length - 1;
        setSelectedIndex(prevIndex);
        // Scroll selected item into view
        setTimeout(() => {
          const selectedElement = document.querySelector(`[data-index="${prevIndex}"]`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ 
              block: 'nearest', 
              behavior: 'smooth' 
            });
          }
        }, 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < products.length) {
          handleProductSelect(products[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowProductDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  }, [showProductDropdown, products, selectedIndex, handleProductSelect]);

  // Handle search focus
  const handleSearchFocus = useCallback(() => {
    if (searchQuery.trim().length >= 2) {
      setShowProductDropdown(true);
    }
  }, [searchQuery]);

  // Add product to cart
  const addToCart = useCallback(() => {
    if (!selectedProduct) {
      toast.error('Please select a product first');
      return;
    }

    const quantityInput = document.getElementById('purchase-quantity-input');
    const uomSelect = document.getElementById('purchase-uom-select');
    const costPriceInput = document.getElementById('purchase-cost-price-input');
    
    if (quantityInput && uomSelect && costPriceInput) {
      const quantity = parseFloat(quantityInput.value) || 1;
      const uom = uomSelect.value;
      const costPrice = parseFloat(costPriceInput.value) || 0;

      const newItem = {
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
        quantity: quantity,
        uom: uom,
        cost_price: costPrice,
        total: quantity * costPrice
      };

      setFormData(prev => {
        // Check if product already exists in cart
        const existingItemIndex = prev.items.findIndex(item => item.product_id === selectedProduct.id);
        
        if (existingItemIndex !== -1) {
          // Remove existing item and add new one at the top
          const updatedItems = prev.items.filter((_, index) => index !== existingItemIndex);
          updatedItems.unshift(newItem); // Add to beginning (top)
          
          return {
            ...prev,
            items: updatedItems
          };
        } else {
          // Add new item at the top
          return {
            ...prev,
            items: [newItem, ...prev.items] // Add to beginning (top)
          };
        }
      });

      // Single toast call after state update
      toast.success('Product added to cart');

      // Reset form
      setSearchQuery('');
      setSelectedProduct(null);
      setCurrentQuantity(0);
      quantityInput.value = '1';
      costPriceInput.value = '0';
      
      // Focus back to search
      setTimeout(() => {
        const searchInput = document.querySelector('input[placeholder*="SEARCH PRODUCTS"]');
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    }
  }, [selectedProduct, toast]);

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { product_id: '', quantity: 1, uom: 'ctn', cost_price: 0 }]
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleCreatePurchase = async () => {
    try {
      setCreateLoading(true);

      // Validate form
      if (!formData.supplier_id) {
        toast.error('Please select a supplier', {
          duration: 3000,
        });
        setCreateLoading(false);
        return;
      }
      if (formData.items.length === 0) {
        toast.error('Please add at least one item', {
          duration: 3000,
        });
        setCreateLoading(false);
        return;
      }
      if (formData.items.some(item => !item.product_id || !item.quantity || !item.cost_price)) {
        toast.error('Please fill in all item details', {
          duration: 3000,
        });
        setCreateLoading(false);
        return;
      }

      const response = await apiService.request('/purchases', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      if (response.success) {
        // Display the generated PO number
        const generatedPONumber = response.data?.purchase_order_number || poNumber;
        if (generatedPONumber) {
          toast.success(`Purchase order ${generatedPONumber} created successfully!`, {
            duration: 3000,
            icon: '✅',
          });
        } else {
          toast.success('Purchase order created successfully!', {
            duration: 3000,
            icon: '✅',
          });
        }
        
        fetchPurchases(); // Refresh the list
        
        // Reset form for next order
        resetFormForNextOrder();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to create purchase order', {
        duration: 3000,
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleApproveOrder = async () => {
    if (!selectedPurchaseOrder || !selectedAction) return;

    try {
      setApproveLoading(true);

      let response;
      if (selectedAction === 'approve') {
        response = await apiService.request(`/purchases/${selectedPurchaseOrder.id}/approve`, {
          method: 'POST'
        });
      } else if (selectedAction === 'reject') {
        response = await apiService.request(`/purchases/${selectedPurchaseOrder.id}/reject`, {
          method: 'POST'
        });
      }

      if (response.success) {
        const actionText = selectedAction === 'approve' ? 'approved' : 'rejected';
        const icon = selectedAction === 'approve' ? '✅' : '❌';
        
        toast.success(`Purchase order ${selectedPurchaseOrder.purchase_order_number} ${actionText} successfully!`, {
          duration: 3000,
          icon: icon,
        });
        
        fetchPurchases(); // Refresh the list
        
        // If approved, open receipt modal; otherwise just close the approve modal
        if (selectedAction === 'approve') {
          handleOpenReceiptModal(selectedPurchaseOrder);
        }
        
        handleCloseApproveModal();
      }
    } catch (err) {
      const actionText = selectedAction === 'approve' ? 'approve' : 'reject';
      toast.error(err.message || `Failed to ${actionText} purchase order`, {
        duration: 3000,
      });
    } finally {
      setApproveLoading(false);
    }
  };

  const handleBulkApproveOrders = async () => {
    if (selectedOrdersForApproval.length === 0) {
      toast.error('Please select at least one order to approve', {
        duration: 3000,
      });
      return;
    }

    try {
      setHeaderApproveLoading(true);
      let successCount = 0;
      let errorCount = 0;

      // Approve each selected order
      for (const orderId of selectedOrdersForApproval) {
        try {
          const response = await apiService.request(`/purchases/${orderId}/approve`, {
            method: 'POST'
          });

          if (response.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (err) {
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} purchase order(s) approved successfully!`, {
          duration: 3000,
          icon: '✅',
        });
      }

      if (errorCount > 0) {
        toast.error(`${errorCount} order(s) failed to approve`, {
          duration: 3000,
        });
      }

      fetchPurchases(); // Refresh the list
      handleCloseHeaderApproveModal();
    } catch (err) {
      toast.error('Failed to approve orders', {
        duration: 3000,
      });
    } finally {
      setHeaderApproveLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      received: { color: 'bg-green-100 text-green-800', icon: Check }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
          {/* Hero Section */}
          <div className="relative overflow-hidden mt-20 bg-gradient-to-br from-red-50 via-white to-blue-50 rounded-3xl p-8 mb-8 shadow-2xl border border-red-200">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-blue-50 opacity-50"></div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-red-100 to-blue-100 rounded-full -translate-y-24 translate-x-24 opacity-30"></div>
            <div className="absolute bottom-0 left-0 w-36 h-36 bg-gradient-to-tr from-blue-100 to-red-100 rounded-full translate-y-18 -translate-x-18 opacity-30"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-500 via-orange-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-300">
                      <ShoppingCart size={32} className="text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                      <Check size={12} className="text-white" />
                    </div>
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-red-600 via-orange-600 to-blue-600 bg-clip-text text-transparent mb-2">
                      Purchase Management
                    </h1>
                    <p className="text-gray-600 text-lg font-medium">
                      Manage purchase orders, approvals, and inventory
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleOpenCreateModal}
                    className="px-6 py-3 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-xl hover:from-red-600 hover:to-blue-700 transition-all duration-200 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <Plus size={20} />
                    Create Order
                  </button>
                  <button
                    onClick={() => {
                      // Open blank approve modal without any order data
                      setSelectedPurchaseOrder(null);
                      setShowApproveModal(true);
                      setApproveSuccess(false);
                      setSelectedAction('');
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <CheckCircle size={20} />
                    Approve Order
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Purchases */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600 mb-1">Total Purchases</p>
                  <p className="text-3xl font-bold text-blue-900">
                    {loading ? <Loader2 className="animate-spin" size={24} /> : purchaseStats.total_purchases || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <ShoppingCart className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            {/* Pending Purchases */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-600 mb-1">Pending</p>
                  <p className="text-3xl font-bold text-yellow-900">
                    {loading ? <Loader2 className="animate-spin" size={24} /> : purchaseStats.pending_purchases || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Clock className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            {/* Approved Purchases */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600 mb-1">Approved</p>
                  <p className="text-3xl font-bold text-green-900">
                    {loading ? <Loader2 className="animate-spin" size={24} /> : purchaseStats.approved_purchases || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            {/* Total Value */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600 mb-1">Total Value</p>
                  <p className="text-3xl font-bold text-purple-900">
                    {loading ? <Loader2 className="animate-spin" size={24} /> : formatCurrency(purchaseStats.total_value || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by order number, supplier, or invoice..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="lg:w-48">
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="received">Received</option>
                </select>
              </div>

              {/* Supplier Filter */}
              <div className="lg:w-48">
                <select
                  value={supplierFilter}
                  onChange={(e) => handleSupplierFilter(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">All Suppliers</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear Filters */}
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setSupplierFilter('');
                  setCurrentPage(1);
                }}
                className="px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 flex items-center gap-2 font-semibold shadow-sm hover:shadow-md transform hover:scale-105"
              >
                <X size={16} />
                Clear
              </button>
            </div>
          </div>

          {/* Purchases Table */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <h3 className="text-lg font-semibold text-gray-900">Purchase Orders</h3>
              <p className="text-sm text-gray-500">Manage your purchase orders and track their status</p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-red-500" size={32} />
                <span className="ml-3 text-gray-600">Loading purchases...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <X className="mx-auto text-red-500" size={48} />
                  <p className="mt-2 text-gray-600">{error}</p>
                  <button
                    onClick={fetchPurchases}
                    className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cost</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Delivery</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {purchases.map((purchase) => (
                        <tr key={purchase.id} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-blue-100 rounded-full flex items-center justify-center">
                                <ShoppingCart size={16} className="text-red-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{purchase.purchase_order_number} - {purchase.supplier?.name || 'Unknown Supplier'}</div>
                                {purchase.supplier_invoice_number && (
                                  <div className="text-sm text-gray-500">Invoice: {purchase.supplier_invoice_number}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{purchase.supplier?.name || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{purchase.contact_person || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(purchase.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{formatCurrency(purchase.total_cost)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {purchase.expected_delivery_date 
                                ? new Date(purchase.expected_delivery_date).toLocaleDateString()
                                : 'N/A'
                              }
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {new Date(purchase.created_at).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleOpenViewModal(purchase)}
                                className="group/btn relative p-2 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                title="View Details"
                              >
                                <Eye size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                  View
                                </span>
                              </button>
                              {purchase.status === 'approved' && (
                                <button
                                  onClick={() => handleOpenReceiptModal(purchase)}
                                  className="group/btn relative p-2 text-purple-600 bg-purple-100 hover:bg-purple-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                  title="Print Receipt"
                                >
                                  <Printer size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    Receipt
                                  </span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-700">
                        Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalItems)} of {totalItems} results
                      </span>
                      <select
                        value={perPage}
                        onChange={(e) => handlePerPageChange(Number(e.target.value))}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      >
                        <option value={10}>10 per page</option>
                        <option value={15}>15 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                      </select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        <ArrowLeft size={16} className="inline mr-1" />
                        Previous
                      </button>
                      <span className="px-4 py-2 text-sm text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl font-medium">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        Next
                        <ArrowRight size={16} className="inline ml-1" />
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
      </div>

      {/* Create Purchase Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-500 to-blue-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleCreatePurchase}
                  disabled={createLoading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200 disabled:opacity-50"
                >
                  <Save size={16} />
                  Save
                </button>
                <div className="px-4 py-2 bg-gray-700 text-white rounded-lg font-mono text-sm flex items-center gap-2">
                  {poNumber === 'Generating...' ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Generating...
                    </>
                  ) : poNumber ? (
                    poNumber
                  ) : (
                    'No PO Number'
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="relative w-64 supplier-dropdown-container">
                  {/* Main Dropdown Button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!showSupplierDropdown) {
                        setFilteredSuppliers(suppliers);
                        setSupplierSearchQuery('');
                      }
                      setShowSupplierDropdown(!showSupplierDropdown);
                    }}
                    className="w-full px-4 py-2 bg-gradient-to-r from-red-500 to-blue-600 border border-red-400 text-white rounded-lg focus:ring-2 focus:ring-red-400 focus:border-transparent text-left flex items-center justify-between"
                  >
                    <span className={selectedSupplier ? 'text-white' : 'text-white/70'}>
                      {selectedSupplier ? selectedSupplier.name : 'Choose a supplier...'}
                    </span>
                    <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Supplier Dropdown Panel */}
                  {showSupplierDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 overflow-hidden">
                      {/* Search Bar Inside Dropdown */}
                      <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <input
                            type="text"
                            value={supplierSearchQuery}
                            onChange={handleSupplierSearchChange}
                            onKeyDown={handleSupplierKeyDown}
                            placeholder="Search suppliers..."
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900 placeholder-gray-500"
                          />
                        </div>
                      </div>
                      
                      {/* Item Count */}
                      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600">
                        {filteredSuppliers.length} of {suppliers.length} suppliers
                      </div>
                      
                      {/* Scrollable Supplier List */}
                      <div className="max-h-48 overflow-y-auto">
                        {filteredSuppliers.map((supplier, index) => (
                          <div
                            key={supplier.id}
                            className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 ${
                              index === supplierSelectedIndex 
                                ? 'bg-blue-100 border-blue-300' 
                                : 'hover:bg-gray-50'
                            }`}
                            onClick={() => handleSupplierSelect(supplier)}
                          >
                            <div className="text-sm font-medium text-gray-900">
                              {supplier.name}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleCloseCreateModal}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <CloseIcon size={16} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Success Message */}
              {createSuccess && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-xl">
                  <div className="flex items-center">
                    <CheckCircle size={20} className="mr-2" />
                    Purchase order created successfully! Closing modal...
                  </div>
                </div>
              )}


              {/* Company and Supplier Information - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Company's Information */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <Building2 size={16} className="text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Company's Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Company (Store) Name
                        </label>
                        <input
                          type="text"
                          value={companySettings.company_name}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Purchase Date
                        </label>
                        <input
                          type="date"
                          value={new Date().toISOString().slice(0, 10)}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                        Expected Delivery Date
                        </label>
                        <input
                            type="date"
                            value={formData.expected_delivery_date}
                            onChange={(e) => handleFormChange('expected_delivery_date', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            min={new Date().toISOString().slice(0, 10)}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Purchase by / Agent
                        </label>
                        <input
                          type="text"
                          value={user?.name || user?.username || user?.email || 'System User'}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Mode of Purchase
                        </label>
                        <select
                          value={formData.mode_of_purchase}
                          onChange={(e) => handleFormChange('mode_of_purchase', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        >
                          <option value="">Select mode</option>
                          <option value="cash">Cash</option>
                          <option value="credit">Credit</option>
                          <option value="bank_transfer">Bank Transfer</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Remark
                        </label>
                        <input
                            type="text"
                            value={formData.remark}
                            onChange={(e) => handleFormChange('remark', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="Optional note..."
                          />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Supplier's Information */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <Building2 size={16} className="text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Supplier's Information</h3>
                  </div>
                  
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Supplier's ID
                        </label>
                        <input
                          type="text"
                          value={selectedSupplier?.supplierID || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Email (optional)
                        </label>
                        <input
                          type="email"
                          value={selectedSupplier?.email || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Supplier's Address
                        </label>
                        <input
                          type="text"
                          value={selectedSupplier?.address || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={selectedSupplier?.phone || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Outstanding Amount
                        </label>
                        <input
                          type="text"
                          value={formatCurrency(outstandingBalance)}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Search Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
                
                <div className="space-y-3">
                  {/* Labels Row */}
                  <div className="flex items-center space-x-4">
                    <div className="w-2/3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Search Products
                      </label>
                    </div>
                    <div className="w-20">
                      <label className="block text-xs font-medium text-gray-700 mb-1">(Qty)</label>
                    </div>
                    <div className="w-20">
                      <label className="block text-xs font-medium text-gray-700 mb-1">(UoM)</label>
                    </div>
                    <div className="w-24">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Cost Price</label>
                    </div>
                    <div className="w-24">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Current Qty</label>
                    </div>
                  </div>
                  
                  {/* Input Fields Row */}
                  <div className="flex items-center space-x-4 relative">
                    <div className="w-2/3 relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown}
                        onFocus={handleSearchFocus}
                        placeholder="SEARCH PRODUCTS BY NAME AND PRODUCTID..."
                        className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm placeholder-gray-500"
                      />
                      
                      {/* Product Dropdown */}
                      {products.length > 0 && showProductDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
                          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600 font-medium">
                            {searchQuery.trim() ? `Found ${products.length} products` : `Showing all ${products.length} products`}
                          </div>
                          {products.map((product, index) => (
                            <div
                              key={product.id}
                              data-index={index}
                              className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 ${
                                index === selectedIndex 
                                  ? (selectedIndex % 2 === 0 ? 'bg-red-100 border-red-300' : 'bg-blue-100 border-blue-300')
                                  : 'hover:bg-gray-50'
                              }`}
                              onClick={() => handleProductSelect(product)}
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 text-sm leading-tight">
                                    {product.name}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-medium text-gray-900">
                                    {formatCurrency(product.cost_price || product.selling_price || product.price)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="w-20">
                      <input
                        id="purchase-quantity-input"
                        type="number"
                        defaultValue="1"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const uomSelect = document.getElementById('purchase-uom-select');
                            if (uomSelect) {
                              uomSelect.focus();
                            }
                          }
                        }}
                        className="w-full px-2 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg text-sm text-center"
                      />
                    </div>
                    <div className="w-20">
                      <select 
                        id="purchase-uom-select" 
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            addToCart();
                          }
                        }}
                        className="w-full px-2 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="ctn">Ctn</option>
                        <option value="pcs">Pcs</option>
                        <option value="kg">Kg</option>
                      </select>
                    </div>
                    <div className="w-24">
                      <input
                        id="purchase-cost-price-input"
                        type="number"
                        placeholder="0.00"
                        className="w-full px-2 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg text-sm text-center"
                      />
                    </div>
                    <div className="w-24">
                      <input
                        id="purchase-current-qty-input"
                        type="number"
                        value={currentQuantity}
                        readOnly
                        className="w-full px-2 py-2 bg-gray-50 text-gray-600 border border-gray-300 rounded-lg text-sm text-center"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Cart/Table Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                      <ShoppingCart size={16} className="text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Purchase Items</h3>
                  </div>
                  
                
                {formData.items.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Package size={32} className="text-white" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">NEXAPOS</h4>
                    <p className="text-gray-600 mb-1">No products added yet</p>
                    <p className="text-sm text-gray-500">Search and add products to create your purchase order</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    {/* Fixed Header */}
                    <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
                      <div className="grid grid-cols-12 gap-4 px-6 py-4 text-sm font-semibold uppercase tracking-wide">
                        <div className="col-span-4">Product</div>
                        <div className="col-span-1 text-center">Qty</div>
                        <div className="col-span-1 text-center">UOM</div>
                        <div className="col-span-2 text-right">Price</div>
                        <div className="col-span-2 text-right">Amount</div>
                        <div className="col-span-2 text-right">Action</div>
                      </div>
                    </div>
                    
                    {/* Scrollable Body */}
                    <div className="max-h-64 overflow-y-auto bg-white">
                      <div className="divide-y divide-gray-100">
                        {formData.items.map((item, index) => (
                          <div key={index} className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-slate-50 transition-colors duration-150">
                            <div className="col-span-4">
                              <div className="text-sm font-medium text-gray-900 leading-tight">
                                {item.product_name || 'Unknown Product'}
                              </div>
                            </div>
                            <div className="col-span-1 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                                {item.quantity}
                              </span>
                            </div>
                            <div className="col-span-1 text-center">
                              <span className="text-sm text-gray-600 font-medium">
                                {item.uom}
                              </span>
                            </div>
                            <div className="col-span-2 text-right">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrency(item.cost_price)}
                              </div>
                            </div>
                            <div className="col-span-2 text-right">
                              <div className="text-sm font-bold text-gray-900">
                                {formatCurrency(item.total)}
                              </div>
                            </div>
                            <div className="col-span-2 text-right">
                              <button
                                onClick={() => handleRemoveItem(index)}
                                className="inline-flex items-center justify-center w-8 h-8 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 hover:text-red-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                title="Remove item"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gradient-to-r from-red-500 to-blue-600 text-white p-4">
              <div className="flex items-center justify-between space-x-8">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-white/80">Items:</span>
                  <span className="text-lg font-bold text-white">{formData.items.length}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-white/80">Total Quantity:</span>
                  <span className="text-lg font-bold text-white">
                    {formData.items.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-white/80">Subtotal:</span>
                  <span className="text-lg font-bold text-white">
                    {formatCurrency(formData.items.reduce((sum, item) => sum + (item.total || 0), 0))}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-bold text-white">Total Amount:</span>
                  <span className="text-2xl font-bold text-yellow-300">
                    {formatCurrency(formData.items.reduce((sum, item) => sum + (item.total || 0), 0))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve Purchase Order Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-500 to-blue-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleApproveOrder}
                  disabled={approveLoading || !selectedPurchaseOrder || !selectedAction}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 font-semibold transition-all duration-200 disabled:opacity-50 ${
                    selectedAction === 'approve' 
                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                      : selectedAction === 'reject'
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                      : 'bg-gray-500 text-white cursor-not-allowed'
                  }`}
                >
                  {selectedAction === 'approve' ? (
                    <>
                      <CheckCircle size={16} />
                      Approve Order
                    </>
                  ) : selectedAction === 'reject' ? (
                    <>
                      <X size={16} />
                      Reject Order
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Select Action
                    </>
                  )}
                </button>
                <div className="relative">
                  <select
                    value={selectedPurchaseOrder?.purchase_order_number || ''}
                    onChange={(e) => {
                      const selectedPO = e.target.value;
                      if (selectedPO) {
                        const order = purchases.find(p => p.purchase_order_number === selectedPO);
                        if (order) {
                          setSelectedPurchaseOrder(order);
                        }
                      } else {
                        setSelectedPurchaseOrder(null);
                      }
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-900 font-semibold rounded-lg font-mono text-sm border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Order to Approve</option>
                    {purchases
                      .filter(purchase => purchase.status === 'pending')
                      .map((purchase) => (
                        <option key={purchase.id} value={purchase.purchase_order_number}>
                          {purchase.purchase_order_number} - {purchase.supplier?.name || 'Unknown Supplier'}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <select
                    value={selectedAction || ''}
                    onChange={(e) => setSelectedAction(e.target.value)}
                    className="px-4 py-2 bg-gradient-to-r from-red-500 to-blue-600 border border-red-400 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Action</option>
                    <option value="approve">Approve</option>
                    <option value="reject">Reject</option>
                  </select>
                </div>
                <button
                  onClick={handleCloseApproveModal}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <CloseIcon size={16} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Success Message */}
              {approveSuccess && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-xl">
                  <div className="flex items-center">
                    <CheckCircle size={20} className="mr-2" />
                    Purchase order approved successfully! Moving to purchases...
                  </div>
                </div>
              )}

              {/* Company and Supplier Information - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Company's Information */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <Building2 size={16} className="text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Company's Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Company (Store) Name
                        </label>
                        <input
                          type="text"
                          value={companySettings.company_name}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Purchase Date
                        </label>
                        <input
                          type="date"
                          value={selectedPurchaseOrder ? new Date(selectedPurchaseOrder.created_at).toISOString().slice(0, 10) : ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                        Expected Delivery Date
                        </label>
                        <input
                            type="date"
                            value={selectedPurchaseOrder?.expected_delivery_date || ''}
                            readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Purchase by / Agent
                        </label>
                        <input
                          type="text"
                          value={selectedPurchaseOrder?.agent || 'System User'}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Mode of Purchase
                        </label>
                        <input
                          type="text"
                          value={selectedPurchaseOrder?.mode_of_purchase || 'Not specified'}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Remark
                        </label>
                        <input
                            type="text"
                            value={selectedPurchaseOrder?.remark || 'No remarks'}
                            readOnly
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                          />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Supplier's Information */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <Building2 size={16} className="text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Supplier's Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Supplier's ID
                        </label>
                        <input
                          type="text"
                          value={selectedPurchaseOrder?.supplierID || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Email (optional)
                        </label>
                        <input
                          type="email"
                          value={selectedPurchaseOrder?.supplier_email || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Supplier's Address
                        </label>
                        <input
                          type="text"
                          value={selectedPurchaseOrder?.supplier_address || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Supplier's Name
                        </label>
                        <input
                          type="tel"
                          value={selectedPurchaseOrder?.supplier?.name || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={selectedPurchaseOrder?.supplier_phone || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Outstanding Amount
                        </label>
                        <input
                          type="text"
                          value={formatCurrency(Number(selectedPurchaseOrder?.supplier?.balance) || 0)}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Search Section - Read Only Display */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                    <Search size={16} className="text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Order Items Summary</h3>
                </div>
                
                <div className="space-y-3">
                  {/* Labels Row */}
                  <div className="flex items-center space-x-4">
                    <div className="w-2/3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Product Name
                      </label>
                    </div>
                    <div className="w-20">
                      <label className="block text-xs font-medium text-gray-700 mb-1">(Qty)</label>
                    </div>
                    <div className="w-20">
                      <label className="block text-xs font-medium text-gray-700 mb-1">(UoM)</label>
                    </div>
                    <div className="w-24">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Cost Price</label>
                    </div>
                    <div className="w-24">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Purchase Items Table */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                    <ShoppingCart size={16} className="text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Purchase Items</h3>
                </div>
                
                {!selectedPurchaseOrder || !selectedPurchaseOrder.items || selectedPurchaseOrder.items.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Package size={32} className="text-white" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">NEXAPOS</h4>
                    <p className="text-gray-600 mb-1">No items in this order</p>
                    <p className="text-sm text-gray-500">This order contains no purchase items</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    {/* Fixed Header */}
                    <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
                      <div className="grid grid-cols-12 gap-4 px-6 py-4 text-sm font-semibold uppercase tracking-wide">
                        <div className="col-span-4">Product</div>
                        <div className="col-span-1 text-center">Qty</div>
                        <div className="col-span-1 text-center">UOM</div>
                        <div className="col-span-2 text-right">Price</div>
                        <div className="col-span-2 text-right">Amount</div>
                        <div className="col-span-2 text-center">Status</div>
                      </div>
                    </div>
                    
                    {/* Scrollable Body */}
                    <div className="max-h-64 overflow-y-auto bg-white">
                      <div className="divide-y divide-gray-100">
                        {selectedPurchaseOrder.items?.map((item, index) => (
                          <div key={index} className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-slate-50 transition-colors duration-150">
                            <div className="col-span-4">
                              <div className="text-sm font-medium text-gray-900 leading-tight">
                                {item.product?.name || 'Unknown Product'}
                              </div>
                            </div>
                            <div className="col-span-1 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                                {item.quantity}
                              </span>
                            </div>
                            <div className="col-span-1 text-center">
                              <span className="text-sm text-gray-600 font-medium">
                                {item.uom || 'N/A'}
                              </span>
                            </div>
                            <div className="col-span-2 text-right">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrency(item.cost_price)}
                              </div>
                            </div>
                            <div className="col-span-2 text-right">
                              <div className="text-sm font-bold text-gray-900">
                                {formatCurrency(item.subtotal)}
                              </div>
                            </div>
                            <div className="col-span-2 text-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <Clock size={12} className="mr-1" />
                                Pending
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gradient-to-r from-red-500 to-blue-600 text-white p-4">
              <div className="flex items-center justify-between space-x-8">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-white/80">Items:</span>
                  <span className="text-lg font-bold text-white">{selectedPurchaseOrder?.items?.length || 0}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-white/80">Total Quantity:</span>
                  <span className="text-lg font-bold text-white">
                    {selectedPurchaseOrder?.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-white/80">Subtotal:</span>
                  <span className="text-lg font-bold text-white">
                    {formatCurrency(Number(selectedPurchaseOrder?.total_cost) || 0)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-bold text-white">Total Amount:</span>
                  <span className="text-2xl font-bold text-yellow-300">
                    {formatCurrency(Number(selectedPurchaseOrder?.total_cost) || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header Approve Orders Modal */}
      {showHeaderApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-500 to-blue-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleBulkApproveOrders}
                  disabled={headerApproveLoading || selectedOrdersForApproval.length === 0}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200 disabled:opacity-50"
                >
                  <CheckCircle size={16} />
                  {headerApproveLoading ? 'Approving...' : `Approve ${selectedOrdersForApproval.length} Order(s)`}
                </button>
                <div className="px-4 py-2 bg-gray-700 text-white rounded-lg font-mono text-sm">
                  {pendingOrders.length} Pending Orders
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="px-4 py-2 bg-gradient-to-r from-red-500 to-blue-600 border border-red-400 text-white rounded-lg">
                  Bulk Approval
                </div>
                <button
                  onClick={handleCloseHeaderApproveModal}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <CloseIcon size={16} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Select All */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedOrdersForApproval.length === pendingOrders.length && pendingOrders.length > 0}
                    onChange={handleSelectAllOrders}
                    className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                  />
                  <span className="text-lg font-semibold text-gray-900">
                    Select All ({pendingOrders.length} orders)
                  </span>
                </label>
              </div>

              {/* Orders List */}
              {pendingOrders.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">No Pending Orders</h4>
                  <p className="text-gray-600 mb-1">All purchase orders have been approved</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingOrders.map((order) => (
                    <div key={order.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <input
                            type="checkbox"
                            checked={selectedOrdersForApproval.includes(order.id)}
                            onChange={() => handleSelectOrderForApproval(order.id)}
                            className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                          />
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-mono text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                {order.purchase_order_number} - {order.supplier?.name || 'Unknown Supplier'}
                              </span>
                              <span className="text-sm text-gray-500">
                                {new Date(order.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="text-lg font-semibold text-gray-900">
                              {order.supplier?.name || 'Unknown Supplier'}
                            </div>
                            <div className="text-sm text-gray-600">
                              {order.items?.length || 0} items • Total: {formatCurrency(order.total_cost)}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500">
                            Agent: {order.agent || 'System'}
                          </div>
                          <div className="text-sm text-gray-500">
                            Expected: {order.expected_delivery_date ? new Date(order.expected_delivery_date).toLocaleDateString() : 'Not set'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gradient-to-r from-red-500 to-blue-600 text-white p-4">
              <div className="flex items-center justify-between space-x-8">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-white/80">Selected Orders:</span>
                  <span className="text-lg font-bold text-white">
                    {selectedOrdersForApproval.length} of {pendingOrders.length}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-white/80">Total Orders:</span>
                  <span className="text-lg font-bold text-white">{pendingOrders.length}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-white/80">Ready to Approve:</span>
                  <span className="text-lg font-bold text-white">{selectedOrdersForApproval.length}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-bold text-white">Status:</span>
                  <span className="text-2xl font-bold text-yellow-300">
                    {selectedOrdersForApproval.length > 0 
                      ? `Ready to Approve`
                      : 'Select Orders'
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && receiptData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <CheckCircle size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Order Approved</h2>
                  <p className="text-green-100 text-sm">Purchase Order Receipt</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="px-4 py-2 bg-white/20 text-white rounded-lg font-mono text-sm">
                  {receiptData.purchase_order_number} - {receiptData.supplier?.name || 'Unknown Supplier'}
                </div>
                <button
                  onClick={handleCloseReceiptModal}
                  className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                >
                  <CloseIcon size={16} />
                </button>
              </div>
            </div>

            {/* Receipt Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              <div className="receipt-content bg-white rounded-xl shadow-sm p-8 max-w-4xl mx-auto">
                {/* Company Header */}
                <div className="text-center mb-8 border-b-2 border-gray-200 pb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">N</span>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-1">NEXAPOS</h1>
                  <p className="text-gray-600">Purchase Order Receipt</p>
                  
                </div>

                {/* Order Details - Compact */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  {/* Company Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                      Company Information
                    </h3>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Company:</span>
                        <span className="text-gray-900 font-medium">{companySettings.company_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Order Date:</span>
                        <span className="text-gray-900">{new Date(receiptData.created_at).toLocaleDateString('en-GB')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Expected Delivery:</span>
                        <span className="text-gray-900">
                          {receiptData.expected_delivery_date 
                            ? new Date(receiptData.expected_delivery_date).toLocaleDateString('en-GB')
                            : 'Not specified'
                          }
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Agent:</span>
                        <span className="text-gray-900">{receiptData.agent || 'System User'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Supplier Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                      Supplier Information
                    </h3>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Supplier:</span>
                        <span className="text-gray-900 font-semibold">{receiptData.supplier?.name || 'Unknown'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Supplier ID:</span>
                        <span className="text-gray-900">{receiptData.supplierID || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Phone:</span>
                        <span className="text-gray-900">{receiptData.supplier_phone || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Email:</span>
                        <span className="text-gray-900">{receiptData.supplier_email || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Address:</span>
                        <span className="text-gray-900">{receiptData.supplier_address || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                    Order Items
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-900">
                            Product
                          </th>
                          <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-900">
                            Qty
                          </th>
                          <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-900">
                            UOM
                          </th>
                          <th className="border border-gray-200 px-4 py-3 text-right text-sm font-semibold text-gray-900">
                            Unit Price
                          </th>
                          <th className="border border-gray-200 px-4 py-3 text-right text-sm font-semibold text-gray-900">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {receiptData.items?.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="border border-gray-200 px-4 py-3 text-sm text-gray-900">
                              {item.product?.name || 'Unknown Product'}
                            </td>
                            <td className="border border-gray-200 px-4 py-3 text-center text-sm text-gray-900">
                              {item.quantity}
                            </td>
                            <td className="border border-gray-200 px-4 py-3 text-center text-sm text-gray-900">
                              {item.uom || 'pcs'}
                            </td>
                            <td className="border border-gray-200 px-4 py-3 text-right text-sm text-gray-900">
                              Ghs {Number(item.cost_price || 0).toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </td>
                            <td className="border border-gray-200 px-4 py-3 text-right text-sm text-gray-900 font-semibold">
                              Ghs {Number(item.subtotal || 0).toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="border-t-2 border-gray-200 pt-6">
                  <div className="flex justify-end">
                    <div className="w-80">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
                          <span className="text-2xl font-bold text-green-600">
                            Ghs {Number(receiptData.total_cost || 0).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                  <p className="text-sm text-gray-500">
                    This is an approved purchase order. Please deliver the items as specified.
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Generated on {new Date().toLocaleString('en-GB')} by NEXAPOS System
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-100 p-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Order Status:</span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                  Approved
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handlePrintReceipt}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200"
                >
                  <Printer size={16} />
                  Print
                </button>
                <button
                  onClick={generatePDF}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200"
                >
                  <FileText size={16} />
                  Generate PDF
                </button>
                <button
                  onClick={handleSendWhatsApp}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200"
                >
                  <CheckCircle size={16} />
                  Send WhatsApp
                </button>
                <button
                  onClick={handleCloseReceiptModal}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200"
                >
                  <CloseIcon size={16} />
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Eye size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Purchase Order Details</h2>
                  <p className="text-blue-100 text-sm font-medium">PO: {viewData.purchase_order_number} - {viewData.supplier?.name || 'Unknown Supplier'}</p>
                </div>
              </div>
              <button
                onClick={handleCloseViewModal}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all duration-200 flex items-center justify-center"
              >
                <CloseIcon size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-8">
              {/* Company Header */}
              <div className="text-center mb-10">
                <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Receipt size={40} className="text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-3">{companySettings.company_name}</h1>
                <p className="text-base text-gray-600 font-medium">Purchase Order Details</p>
              </div>

              {/* Order Details */}
              <div className="grid grid-cols-2 gap-8 mb-8">
                {/* Company Information */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 shadow-sm border border-gray-200">
                  <h3 className="text-sm font-bold text-gray-800 mb-4 uppercase tracking-wide">Company Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Company:</span>
                      <span className="text-gray-900 font-bold">{companySettings.company_name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Order Date:</span>
                      <span className="text-gray-900 font-bold">{new Date(viewData.created_at).toLocaleDateString('en-GB')}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Expected Delivery:</span>
                      <span className="text-gray-900 font-bold">{viewData.expected_delivery_date ? new Date(viewData.expected_delivery_date).toLocaleDateString('en-GB') : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Agent:</span>
                      <span className="text-gray-900 font-bold">{viewData.agent || 'System User'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Status:</span>
                      <span className="text-gray-900 font-bold capitalize">{viewData.status}</span>
                    </div>
                  </div>
                </div>

                {/* Supplier Information */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 shadow-sm border border-gray-200">
                  <h3 className="text-sm font-bold text-gray-800 mb-4 uppercase tracking-wide">Supplier Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Supplier:</span>
                      <span className="text-gray-900 font-bold">{viewData.supplier?.name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Supplier ID:</span>
                      <span className="text-gray-900 font-bold">{viewData.supplierID || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Phone:</span>
                      <span className="text-gray-900 font-bold">{viewData.supplier_phone || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Email:</span>
                      <span className="text-gray-900 font-bold">{viewData.supplier_email || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Address:</span>
                      <span className="text-gray-900 font-bold">{viewData.supplier_address || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Order Items</h3>
                <div className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b-2 border-gray-200">
                    <div className="grid grid-cols-6 gap-4 text-xs font-bold text-gray-800 uppercase tracking-wide">
                      <div>Product</div>
                      <div className="text-center">Quantity</div>
                      <div className="text-center">UOM</div>
                      <div className="text-right">Price</div>
                      <div className="text-right">Amount</div>
                      <div className="text-center">Status</div>
                    </div>
                  </div>
                  <div className="divide-y-2 divide-gray-100">
                    {viewData.items && viewData.items.length > 0 ? (
                      viewData.items.map((item, index) => (
                        <div key={index} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                        <div className="grid grid-cols-6 gap-4 items-center text-sm">
                            <div className="font-bold text-gray-900 text-sm">
                            {item.product?.name || 'Unknown Product'}
                          </div>
                          <div className="text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-sm font-bold rounded-lg shadow-sm">
                              {item.quantity}
                            </span>
                          </div>
                            <div className="text-center text-gray-700 font-medium text-sm">
                            {item.uom || 'pcs'}
                          </div>
                            <div className="text-right font-bold text-gray-900 text-sm">
                              {formatCurrency(item.cost_price)}
                          </div>
                            <div className="text-right font-bold text-gray-900 text-sm">
                              {formatCurrency(item.subtotal)}
                          </div>
                          <div className="text-center">
                              <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold ${
                              viewData.status === 'approved' ? 'bg-green-100 text-green-800' :
                              viewData.status === 'received' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                                <CheckCircle size={14} className="mr-1" />
                              {viewData.status === 'approved' ? 'Approved' :
                               viewData.status === 'received' ? 'Received' :
                               'Pending'}
                            </span>
                          </div>
                        </div>
                      </div>
                      ))
                    ) : (
                      <div className="px-6 py-12 text-center">
                        <div className="text-gray-400 mb-4">
                          <Receipt size={48} className="mx-auto" />
                  </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Items Found</h3>
                        <p className="text-gray-600">This purchase order doesn't have any items yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 shadow-sm border border-gray-200 mb-8">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Total Amount:</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {formatCurrency(viewData.total_cost)}
                  </span>
                </div>
              </div>

              {/* Order Status */}
              <div className="text-center">
                <div className={`inline-flex items-center px-6 py-3 rounded-2xl text-sm font-bold shadow-lg ${
                  viewData.status === 'approved' ? 'bg-green-100 text-green-800' :
                  viewData.status === 'received' ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  <CheckCircle size={20} className="mr-2" />
                  {viewData.status === 'approved' ? 'Order Approved' :
                   viewData.status === 'received' ? 'Order Received' :
                   'Order Pending Approval'}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 flex items-center justify-between border-t-2 border-gray-200">
              <div className="text-sm text-gray-600 font-medium">
                Order viewed on {new Date().toLocaleString()}
              </div>
              <div className="flex items-center space-x-4">
                {viewData.status === 'approved' && (
                  <button
                    onClick={() => {
                      handleCloseViewModal();
                      handleOpenReceiptModal(viewData);
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl flex items-center gap-3 font-bold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <Printer size={18} />
                    Print Receipt
                  </button>
                )}
                <button
                  onClick={handleCloseViewModal}
                  className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-xl flex items-center gap-3 font-bold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <CloseIcon size={18} />
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PurchaseManagement;
